import EmptyListIcon from '@root/public/images/shared/BlankSheet.png'
import Image from 'next/image'
import { FC, useState } from 'react'

import { useRequestsSummary } from '@/api/models/requests'
import { QuickFilterAlias } from '@/api/models/requests/lib/requests.types'
import { cn } from '@/app/utils'
import { EmptyPassesPlug } from '@/components/entities/passes/ui/EmptyPassesPlug'
import { PageType } from '@/components/entities/requests/model/types'
import { EmptyRequestsPlug } from '@/components/entities/requests/ui/EmptyRequestsPlug'
import { useQuickFiltersByPageType } from '@/components/entities/requests/ui/QuickFilters/hooks/useQuickFiltersByPageType'
import {
  findQuickFilterByAlias,
  transformQuickFilterParams
} from '@/components/entities/requests/ui/QuickFilters/utils/quickFilters'
import { tabs } from '@/components/entities/requests/ui/RequestSummary/RequestsTypeSelect.constants'
import { RequestsTypeSelectOption } from '@/components/entities/requests/ui/RequestSummary/RequestsTypeSelect.types'
import { ErrorCard, ErrorForbidden } from '@/components/pages/plugs'
import EmptyListPlug from '@/components/pages/plugs/EmptyListPlug'
import { ButtonSkeleton, TableSkeleton } from '@/components/shared/skeleton'
import { RequestDrawer } from '@/components/widgets/request/RequestDrawer'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { CreateRequestButton } from '../CreateRequestButton'
import RequestsList from '../RequestsList/RequestsList'
import RequestsTypeSelector from './RequestsTypeSelector'

type Props = {
  isAccessForbidden: boolean
}

const RequestsSummary: FC<Props> = ({ isAccessForbidden }) => {
  const [selectedTab, setSelectedTab] = useState<RequestsTypeSelectOption>(
    tabs[0]
  )

  const availableQuickFilters = useQuickFiltersByPageType(selectedTab.key)

  const allFilter = findQuickFilterByAlias(
    QuickFilterAlias.All,
    availableQuickFilters
  )
  const allFilters = transformQuickFilterParams(allFilter?.parameters)

  const { data, isLoading, isError, error, arePassesAvailable } =
    useRequestsSummary(selectedTab.key, allFilters)

  const handleSelectedTabChange = (tab: RequestsTypeSelectOption) => {
    setSelectedTab(tab)
  }

  if (isLoading) {
    return (
      <div className="rounded-16 bg-white p-24 md:p-16 sm:bg-transparent sm:px-0">
        <div className="flex flex-col">
          <div className="mb-24 flex gap-56 md:mb-12 md:gap-40 sm:justify-around sm:gap-0">
            <ButtonSkeleton className="h-[36px] w-[115px] md:h-[28px] md:w-[98px] sm:h-[28px] sm:w-[116px]" />
            <ButtonSkeleton className="h-[36px] w-[140px] md:h-[28px] md:w-[81px] sm:h-[28px] sm:w-[116px]" />
          </div>
          <TableSkeleton
            rows={6}
            itemClassName="h-[100px] md:h-[84px] xs:h-[128px]"
          />
        </div>
      </div>
    )
  }

  if (error && isForbiddenError(error)) {
    return (
      <div className="relative flex grow flex-col overflow-hidden rounded-16 bg-white p-32 pb-0 sm:p-16 xxs:h-auto">
        <div className="mb-24 flex h-full justify-center">
          <ErrorForbidden />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="relative flex grow flex-col overflow-hidden rounded-16 bg-white p-32 pb-0 sm:p-16 xxs:h-auto">
        <div className="mb-24 flex h-full justify-center">
          <ErrorCard />
        </div>
      </div>
    )
  }

  const items = data?.length !== undefined && data?.length > 0

  return (
    <>
      <article
        className={cn(
          'relative flex min-h-[200px] grow flex-col overflow-hidden rounded-16 p-24 xl:max-h-[634px]',
          'md:max-h-[587px] md:p-16',
          'sm:max-h-none sm:bg-transparent sm:px-0 sm:py-20',
          isAccessForbidden || isLoading ? 'bg-white' : 'bg-grey-20'
        )}
      >
        {isAccessForbidden ? (
          <ErrorForbidden />
        ) : (
          <>
            <div className="mb-24 flex justify-between md:mb-12">
              <div className="w-full border-b-[1px] border-grey-30">
                <RequestsTypeSelector
                  className="-mb-1"
                  options={tabs}
                  value={selectedTab}
                  onChange={handleSelectedTabChange}
                />
              </div>
            </div>
            <div className="flex h-[calc(100%-64px)] flex-grow flex-col overflow-y-auto">
              {!isLoading &&
                !arePassesAvailable &&
                selectedTab.key === PageType.Passes && (
                  <div className="flex flex-col items-center justify-center">
                    <EmptyListPlug
                      className="mt-0 max-w-[420px] items-center justify-center sm:max-w-[380px]"
                      titleClassName="text-24 xxs:text-16"
                      descriptionClassName="!text-grey-60"
                      image={
                        <Image
                          src={EmptyListIcon.src}
                          alt="empty-list"
                          width={100}
                          height={100}
                        />
                      }
                      title="Заказ пропусков недоступен"
                      description="К сожалению, заказ пропусков недоступен в данный момент. Для оформления пропуска создайте обращение."
                      buttonComponent={<CreateRequestButton size="medium" />}
                    />
                  </div>
                )}
              {items && (
                <RequestsList
                  requests={data}
                  pageType={selectedTab.key}
                  arePassesAvailable={arePassesAvailable}
                />
              )}
              {!items &&
                arePassesAvailable &&
                selectedTab.key === PageType.Passes && (
                  <EmptyPassesPlug arePassesEmpty isCreateButton />
                )}
              {!items && selectedTab.key === PageType.Requests && (
                <EmptyRequestsPlug areRequestsEmpty isCreateButton />
              )}
            </div>
          </>
        )}
      </article>
      <RequestDrawer pageType={selectedTab.key} />
    </>
  )
}

export default RequestsSummary
